<template>
  <div class="question-list">
    <TableList
      :loading="loading"
      :listData="listData"
      :tableColumns="tableColumns"
      :actionBtn="actionBtn"
      :pagination="pagination"
      :defaultSort="defaultSort"
      @pagination-change="handlePaginationChange"
      @sort-change="handleSortChange"
    >
      <template #filter>
        <el-select
          v-model="filters.type"
          clearable
          class="filter-select"
          :placeholder="$t(`${i18nPrefix}.placeholder.type`)"
        >
          <el-option
            v-for="item in questionTypeOptions"
            :key="item.value"
            :label="$t(item.labelI18nKey)"
            :value="item.value"
          />
        </el-select>
        <el-select
          v-model="filters.status"
          clearable
          class="filter-select"
          :placeholder="$t(`${i18nPrefix}.placeholder.status`)"
        >
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="$t(item.labelI18nKey)"
            :value="item.value"
          />
        </el-select>
      </template>
      <template #btn>
        <el-button class="create-btn" @click="openCreateDialog">{{ $t(`${i18nPrefix}.btn.addNewQuestion`) }}</el-button>
      </template>
      <template #type="{ data }">
        {{ getTypeLabel(data) }}
      </template>
      <template #status="{ row }">
        <el-switch
          v-model="row.status"
          active-color="#0096cc"
          inactive-color="#909399"
          :active-value="QUESTION_STATUS.ENABLED"
          :inactive-value="QUESTION_STATUS.DISABLED"
          :disabled="row.isInherited"
          @change="handleStatusChange(row)"
        />
    </template>
  </TableList>
    <QuestionEditDialog
      :show.sync="questionDialog.show"
      :question="questionDialog.question"
      :isEdit="questionDialog.isEdit"
      :loading="questionDialog.loading"
      @saved="handleQuestionSaved"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { HM_QUESTION_STATUS } from '@/constant/questionnaire'
import { getI18nPropertyValue } from '@/i18n'
import QuestionEditDialog from '../components/QuestionEditDialog.vue'
import { QUESTION_TYPE_OPTIONS, getQuestionTypeLabel } from '../components/questionType'

export default {
  components: {
    TableList,
    QuestionEditDialog
  },
  data () {
    return {
      QUESTION_STATUS: HM_QUESTION_STATUS,
      loading: false,
      i18nPrefix: 'admin.company.questionnaireManagement',
      listData: [],
      filters: {
        type: null,
        status: null
      },
      order: '-status',
      isApplyingRouteQuery: false,
      isUpdatingRouteQuery: false,
      pagination: {
        page: 1,
        pageSize: 10,
        pageSizes: [10, 20, 50],
        total: 0,
        layout: 'total, sizes, prev, pager, next, jumper'
      },
      questionDialog: {
        show: false,
        isEdit: false,
        loading: false,
        question: null
      }
    }
  },
  computed: {
    questionTypeOptions () {
      return QUESTION_TYPE_OPTIONS
    },
    statusOptions () {
      return [
        { value: HM_QUESTION_STATUS.ENABLED, labelI18nKey: `${this.i18nPrefix}.status.enabled` },
        { value: HM_QUESTION_STATUS.DISABLED, labelI18nKey: `${this.i18nPrefix}.status.disabled` }
      ]
    },
    defaultSort () {
      return {
        prop: 'status',
        order: this.order === '-status' ? 'descending' : 'ascending'
      }
    },
    tableColumns () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.label.question`),
          dataKey: 'titleCn',
          formatter: (data, row) => getI18nPropertyValue(row, 'titleCn', 'titleEn'),
          width: '348px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.type`),
          dataKey: 'type',
          slotName: 'type',
          width: '232px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'status',
          slotName: 'status',
          sort: {
            enable: 'custom',
            orders: ['descending', 'ascending']
          },
          width: '167px'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.lastUpdate`),
          dataKey: 'updatedAt',
          width: '157px'
        }
      ]
    },
    actionBtn () {
      return {
        edit: {
          show: row => row.canEdit,
          labelI18nKey: 'common.btn.edit',
          onclick: this.openEditDialog
        }
      }
    }
  },
  created () {
    this.applyRouteQuery()
    this.fetchList()
  },
  methods: {
    ...mapActions('questionnaire', {
      searchHmQuestions: 'searchHmQuestions',
      getById: 'getHmQuestionById',
      updateHmQuestionStatus: 'updateHmQuestionStatus'
    }),
    getTypeLabel (type) {
      return getQuestionTypeLabel(this, type)
    },
    formatDate (value) {
      return value ? moment(value).format('YYYY-MM-DD') : ''
    },
    normalizePageTotal (resp) {
      return _.chain(['total_count', 'count', 'total'])
        .map(key => _.get(resp, key))
        .find(total => total !== undefined && total !== null)
        .thru(total => {
          if (total !== undefined && total !== null) {
            return total
          }
          return _.chain(resp)
            .thru(data => {
              if (_.isArray(data)) {
                return data
              }
              return _.get(data, 'data', [])
            })
            .size()
            .value()
        })
        .value()
    },
    normalizePositiveInteger (value, fallback) {
      const numberValue = Number(value)
      return Number.isInteger(numberValue) && numberValue > 0 ? numberValue : fallback
    },
    normalizeListOrder (order) {
      return _.includes(['status', '-status'], order) ? order : '-status'
    },
    applyRouteQuery () {
      const query = this.$route.query || {}
      this.isApplyingRouteQuery = true
      this.filters = {
        type: _.chain(QUESTION_TYPE_OPTIONS).map('value').includes(query.type).value() ? query.type : null,
        status: _.includes([HM_QUESTION_STATUS.ENABLED, HM_QUESTION_STATUS.DISABLED], query.status) ? query.status : null
      }
      this.order = this.normalizeListOrder(query.order)
      this.pagination = {
        ...this.pagination,
        page: this.normalizePositiveInteger(query.page, 1),
        pageSize: this.normalizePositiveInteger(query.pageSize, this.pagination.pageSize)
      }
      this.$nextTick(() => {
        this.isApplyingRouteQuery = false
      })
    },
    buildRouteQuery () {
      return _.omitBy({
        ...this.$route.query,
        type: this.filters.type || undefined,
        status: this.filters.status || undefined,
        order: this.order,
        page: this.pagination.page > 1 ? `${this.pagination.page}` : undefined,
        pageSize: this.pagination.pageSize !== 10 ? `${this.pagination.pageSize}` : undefined
      }, value => value === undefined || value === null || value === '')
    },
    replaceRouteQuery () {
      const query = this.buildRouteQuery()
      if (_.isEqual(this.$route.query, query)) {
        return
      }
      this.isUpdatingRouteQuery = true
      this.$router.replace({ path: this.$route.path, query }).catch(err => err).finally(() => {
        this.isUpdatingRouteQuery = false
      })
    },
    joinOptions (options, key) {
      return _.chain(options || []).map(key).filter().join('\n').value()
    },
    normalizeQuestion (question) {
      const source = question || {}
      const options = source.options || []
      const type = source.question_type
      const isLocked = Boolean(source.is_locked)
      const isInherited = Boolean(source.is_inherited)
      return {
        id: source.id,
        type,
        titleCn: source.title_zh,
        titleEn: source.title_en,
        remark: isInherited ? 'Inherited' : '',
        status: source.status,
        editStatus: isLocked || isInherited ? 'locked' : 'editable',
        usageCount: isLocked ? 1 : 0,
        canEdit: !isLocked && !isInherited,
        isLocked,
        isInherited,
        updatedAt: this.formatDate(source.updated_time),
        optionsCn: this.joinOptions(options, 'label_zh'),
        optionsEn: this.joinOptions(options, 'label_en'),
        raw: source
      }
    },
    buildSearchParams () {
      return {
        include_inherited: true,
        page_num: this.pagination.page,
        page_size: this.pagination.pageSize,
        question_type: this.filters.type,
        status: this.filters.status,
        order: this.order
      }
    },
    async fetchList () {
      this.loading = true
      try {
        const resp = await this.searchHmQuestions(this.buildSearchParams())
        this.listData = _.chain(resp)
          .thru(data => {
            if (_.isArray(data)) {
              return data
            }
            return _.get(data, 'data', [])
          })
          .map(item => this.normalizeQuestion(item))
          .value()
        this.pagination.total = this.normalizePageTotal(resp)
      } finally {
        this.loading = false
      }
    },
    handleQuestionSaved () {
      if (this.questionDialog.isEdit) {
        this.pagination.page = 1
        this.pagination.total = 0
        this.replaceRouteQuery()
      }
      this.fetchList()
    },
    openCreateDialog () {
      this.questionDialog = {
        show: true,
        isEdit: false,
        loading: false,
        question: null
      }
    },
    async openEditDialog (row) {
      this.questionDialog = {
        isEdit: true,
        show: true,
        loading: true,
        question: null
      }
      try {
        const resp = await this.getById(row.id)
        this.questionDialog.question = this.normalizeQuestion(resp)
      } finally {
        this.questionDialog.loading = false
      }
    },
    async handleStatusChange (row) {
      const nextStatus = row.status
      this.loading = true
      try {
        const resp = await this.updateHmQuestionStatus({ id: row.id, status: nextStatus })
        _.assign(row, this.normalizeQuestion(resp))
        this.$message.success(this.$t('common.message.optionSuccess'))
        if (this.filters.status && row.status !== this.filters.status) {
          await this.fetchList()
        }
      } catch (e) {
        row.status = nextStatus === HM_QUESTION_STATUS.ENABLED ? HM_QUESTION_STATUS.DISABLED : HM_QUESTION_STATUS.ENABLED
        this.$message.error(e.message)
      } finally {
        this.loading = false
      }
    },
    handlePaginationChange (pagination) {
      this.pagination = pagination
      this.replaceRouteQuery()
      this.fetchList()
    },
    handleSortChange (prop, order) {
      this.order = `${order}${prop}`
      this.pagination.page = 1
      this.pagination.total = 0
      this.replaceRouteQuery()
      this.fetchList()
    }
  },
  watch: {
    '$route.query' () {
      if (this.isUpdatingRouteQuery) {
        return
      }
      this.applyRouteQuery()
      this.fetchList()
    },
    filters: {
      deep: true,
      handler () {
        if (this.isApplyingRouteQuery) {
          return
        }
        this.pagination.page = 1
        this.pagination.total = 0
        this.replaceRouteQuery()
        this.fetchList()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.question-list {
  .filter-select {
    width: 150px;
  }

  .status-editable {
    color: #0096cc;
  }
}
</style>
